/**
 * useAddressSuggest — debounced hook wrapping GET /api/geocode/suggest.
 *
 * Returns Nominatim + own-project suggestions matching the query. Items:
 *   { kind: 'address'|'project', label, sub, lat, lon, project_id?, bbox? }
 *
 * Reusable in the GeolocationBanner today, and in the future HUB address list
 * (long-term vision: HUB = map + saved addresses list, not the project list).
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAddressSuggest(query, { minChars = 3, delayMs = 250, limit = 6 } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < minChars) { setItems([]); setLoading(false); return undefined; }
    setLoading(true);
    setError(null);
    if (cancelRef.current) cancelRef.current.abort();
    const controller = new AbortController();
    cancelRef.current = controller;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/geocode/suggest`, {
          params: { q, limit },
          withCredentials: true,
          signal: controller.signal,
        });
        setItems(r.data?.items || []);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
          setError(e?.response?.data?.detail || 'Suggestions indisponibles');
          setItems([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, delayMs);
    return () => { clearTimeout(t); controller.abort(); };
  }, [query, minChars, delayMs, limit]);

  return { items, loading, error };
}
